import React, { useState, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import { useMaterialUIController } from "context";
import { formatLocalDateTime } from "utils/dateFormatter";
import { request } from "app/network";
import { useApiMutation } from "hooks/useApiMutation";

import GenericListScreen from "components/GenericListScreen/GenericListScreen";
import { Column } from "components/GenericListScreen/types";

import AddEditFAQModal from "./AddEditFAQModal";
import ReorderFaqModal from "./components/ReorderFaqModal/ReorderFaqModal";

export default function FAQListScreen() {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const queryClient = useQueryClient(); // Access QueryClient

    // State for Modals
    const [modalOpen, setModalOpen] = useState(false);
    const [reorderModalOpen, setReorderModalOpen] = useState(false);
    const [selectedFaq, setSelectedFaq] = useState<any | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [faqToDelete, setFaqToDelete] = useState<any | null>(null);

    // Refresh List Handler
    const handleRefresh = () => {
        queryClient.invalidateQueries({ queryKey: ["Faq.pagedList"] });
    };

    // Delete Mutation
    const deleteMutation = useApiMutation("Faq.delete", {
        onSuccess: () => {
            setDeleteDialogOpen(false);
            setFaqToDelete(null);
            handleRefresh(); // Refresh list after delete
        }
    });

    // ... handle delete click ...
    const handleDeleteClick = (faq: any) => {
        setFaqToDelete(faq);
        setDeleteDialogOpen(true);
    };

    const confirmDelete = () => {
        if (faqToDelete) {
            deleteMutation.mutate({
                pathParams: { id: faqToDelete.seqId.toString() }
            });
        }
    };

    // ... columns definition ...
    const columns: Column<any>[] = useMemo(() => [
        {
            Header: "카테고리",
            accessor: "categoryName",
            Cell: ({ row }: any) => (
                <MDTypography variant="button" fontWeight="medium">
                    {row.original.categoryName || row.original.categoryCode}
                </MDTypography>
            ),
        },
        {
            Header: "질문",
            accessor: "question",
            width: "40%",
            Cell: ({ row }: any) => (
                <MDBox display="flex" alignItems="center">
                    <MDTypography
                        variant="caption"
                        color="text"
                        sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {row.original.question}
                    </MDTypography>
                </MDBox>
            ),
        },
        {
            Header: "상태",
            accessor: "isActive",
            Cell: ({ row }: any) => (
                <Chip
                    label={row.original.isActive ? "활성" : "비활성"}
                    color={row.original.isActive ? "success" : "default"}
                    size="small"
                    sx={{
                        fontWeight: "medium",
                        ...(row.original.isActive && {
                            color: "#ffffff",
                            "& .MuiChip-label": { color: "#ffffff" }
                        }),
                    }}
                />
            ),
        },
        {
            Header: "순서",
            accessor: "displayOrder",
            Cell: ({ value }: any) => (
                <MDTypography variant="caption" color="text">
                    {value}
                </MDTypography>
            ),
        },
        {
            Header: "등록일",
            accessor: "crtDt",
            Cell: ({ value }: any) => (
                <MDTypography variant="caption" color="text">
                    {value ? formatLocalDateTime(value) : "-"}
                </MDTypography>
            ),
        },
        {
            Header: "관리",
            accessor: "actions",
            Cell: ({ row }: any) => (
                <MDBox display="flex" alignItems="center">
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            setSelectedFaq(row.original);
                            setModalOpen(true);
                        }}
                        sx={{ color: darkMode ? '#fff' : 'inherit' }}
                    >
                        <Icon>edit</Icon>
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteClick(row.original);
                        }}
                        sx={{ color: "#f44336" }}
                    >
                        <Icon>delete</Icon>
                    </IconButton>
                </MDBox>
            ),
        },
    ], [darkMode]);

    // Data Transform
    const dataTransform = (data: any) => {
        return {
            items: data.items || [],
            totalCount: data.totalCount || 0
        };
    };

    return (
        <>
            <GenericListScreen
                title="FAQ 목록 ( FAQ LIST )"
                icon="help_outline"
                apiName="Faq.pagedList"
                columns={columns}
                dataTransform={dataTransform}
                actionComponent={
                    <MDBox display="flex" gap={1}>
                        <MDButton variant="outlined" color="info" onClick={() => setReorderModalOpen(true)}>
                            <Icon fontSize="small" sx={{ mr: 0.5 }}>swap_vert</Icon>
                            순서 변경
                        </MDButton>
                        <MDButton variant="gradient" color="info" onClick={() => { setSelectedFaq(null); setModalOpen(true); }}>
                            <Icon fontSize="small" sx={{ mr: 0.5 }}>add</Icon>
                            FAQ 추가
                        </MDButton>
                    </MDBox>
                }
            />

            {/* Modals */}
            <AddEditFAQModal
                open={modalOpen}
                onClose={() => { setModalOpen(false); setSelectedFaq(null); }}
                onSuccess={handleRefresh}
                darkMode={darkMode}
                faq={selectedFaq}
            />

            <ReorderFaqModal
                open={reorderModalOpen}
                onClose={() => setReorderModalOpen(false)}
                onSuccess={handleRefresh}
                darkMode={darkMode}
            />

            {/* Delete Confirmation */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                PaperProps={{
                    sx: {
                        backgroundColor: darkMode ? "#1a2035" : "#fff",
                        color: darkMode ? "#fff" : "#000",
                    },
                }}
            >
                <DialogTitle>FAQ 삭제</DialogTitle>
                <DialogContent>
                    <MDTypography variant="body2">
                        "{faqToDelete?.question}" FAQ를 삭제하시겠습니까?
                    </MDTypography>
                </DialogContent>
                <DialogActions>
                    <MDButton onClick={() => setDeleteDialogOpen(false)} color="dark">취소</MDButton>
                    <MDButton onClick={confirmDelete} color="error" variant="gradient">삭제</MDButton>
                </DialogActions>
            </Dialog>
        </>
    );
}
